// Meta Pixel (Facebook Pixel) helper.
// The base pixel + initial PageView are loaded in public/index.html.
// This module provides typed helpers for SPA navigation + conversion events.

const ENABLED = !!process.env.REACT_APP_META_PIXEL_ID;

function fbq(...args) {
  if (!ENABLED) return;
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  try {
    window.fbq(...args);
  } catch (e) {
    // never crash the app for a pixel error
    // eslint-disable-next-line no-console
    console.warn("Meta Pixel error:", e);
  }
}

/**
 * Fire a PageView. The base pixel auto-fires one on first load; call this on
 * every client-side route change so SPA navigations get tracked too.
 */
export function trackPageView() {
  fbq("track", "PageView");
}

/**
 * Fire InitiateCheckout when a buyer reaches the on-site checkout step.
 * @param {{ value:number, currency?:string, content_name?:string, content_ids?:string[], event_id?:string }} params
 */
export function trackInitiateCheckout({ value, currency = "GBP", content_name, content_ids, event_id }) {
  if (!Number.isFinite(value)) return;
  fbq("track", "InitiateCheckout", {
    value,
    currency,
    ...(content_name ? { content_name } : {}),
    ...(content_ids ? { content_ids, content_type: "product" } : {}),
  }, event_id ? { eventID: event_id } : undefined);
}

/**
 * Fire Purchase exactly once per successful Stripe session.
 * Uses sessionStorage to dedupe in case the user refreshes /checkout-success.
 *
 * @param {{ value:number, currency?:string, content_ids?:string[], content_name?:string, transaction_id:string, event_id?:string }} params
 */
export function trackPurchase({ value, currency = "GBP", content_ids, content_name, transaction_id, event_id }) {
  if (!Number.isFinite(value) || !transaction_id) return;
  const key = `mp_purchase_${transaction_id}`;
  try {
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, "1");
  } catch (e) { /* ignore quota / private mode errors */ }
  fbq("track", "Purchase", {
    value,
    currency,
    ...(content_ids ? { content_ids, content_type: "product" } : {}),
    ...(content_name ? { content_name } : {}),
    transaction_id,
  }, event_id ? { eventID: event_id } : undefined);
}

/**
 * Fire Lead on successful registration (registration without an immediate purchase).
 * The backend mirrors this via Meta CAPI using the same event_id for dedup.
 * @param {{ event_id:string, content_name?:string }} params
 */
export function trackLead({ event_id, content_name = "registration" }) {
  if (!event_id) return;
  fbq("track", "Lead", { content_name }, { eventID: event_id });
}

export const META_PIXEL_ENABLED = ENABLED;
